/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Icon,
  useColorModeValue,
  Image as CImage,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
  Button,
  Divider,
  Portal,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import {
  FiInfo,
  FiBell,
  FiSmartphone,
  FiMail,
  FiMessageSquare,
  FiCalendar,
  FiHash,
  FiPlay,
  FiDownloadCloud,
  FiBookOpen,
} from 'react-icons/fi';
import { SlEarphones } from 'react-icons/sl';
import { FaDownload } from 'react-icons/fa6';
import { badges } from '../../ModulesSuperApp/steps/Step4';
import { useState } from 'react';
import { useBookStaffDownloadMutation } from '@partiu/api/dashboard/hooks.ts';
import { isMagazineType } from '../utils.ts';

interface BookCardProps {
  book: any;
  size?: 'sm' | 'md' | 'lg';
  showAuthor?: boolean;
  showPages?: boolean;
  showDate?: boolean;
  showHash?: boolean;
  hashCode?: string;
  date?: string;
  description?: string;
  showInfo?: boolean;
}

const formatDateTime = (value?: string) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export function BookCard({
  book,
  showAuthor = false,
  showDate = false,
  showHash = false,
  hashCode,
  date,
  description,
  showInfo = false,
}: BookCardProps) {
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('#ededed', 'gray.600');
  const titleColor = useColorModeValue('gray.800', 'gray.100');
  const subColor = useColorModeValue('#787878', 'gray.400');
  const [audioUrls, setAudioUrls] = useState<string[]>([]);
  const toast = useToast();
  const { isOpen: isAudioModalOpen, onOpen: onOpenAudioModal, onClose: onCloseAudioModal } = useDisclosure();

  const downloadMutation = useBookStaffDownloadMutation({
    onSuccess: (response) => {
      const urls = response?.fileUrls?.length ? response.fileUrls : response?.fileUrl ? [response.fileUrl] : [];

      if (!urls.length) {
        toast({ title: 'Arquivo indisponível', status: 'warning' });
        return;
      }

      if (book?.type === 'audiobook') {
        setAudioUrls(urls);
        onOpenAudioModal();
        return;
      }

      urls.forEach((url) => window.open(url, '_blank'));
    },
    onError: () => {
      toast({ title: 'Erro ao baixar', description: 'Não foi possível obter o arquivo.', status: 'error' });
    },
  });

  const getFileName = (url: string) => {
    try {
      return decodeURIComponent(url.split('/').pop()?.split('?')[0] || '');
    } catch {
      return url;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'magazine':
      case 'magazine-partiu':
        return 'orange';
      case 'book':
        return 'blue';
      case 'audiobook':
        return 'purple';
      default:
        return 'gray';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'magazine':
        return 'Revista';
      case 'magazine-partiu':
        return 'Revista Partiu';
      case 'book':
        return 'Livro';
      case 'audiobook':
        return 'Audio';
      default:
        return 'Título';
    }
  };

  const cover = book?.coverOptimized || book?.cover;
  const effectiveAuthor = book?.author || book?.book?.author;
  const effectiveDate = date || book?.createdAt || book?.book?.createdAt;
  const effectiveDescription = description || book?.description || book?.book?.description;
  const effectiveHash = hashCode || book?.hashCode || book?.id;

  const handleDownload = () => {
    const id = book?.id;
    if (!id || downloadMutation.isLoading) return;
    setAudioUrls([]);
    downloadMutation.mutate(id);
  };

  return (
    <Box
      bg={cardBg}
      borderRadius="10px"
      p="8px"
      border="1px solid"
      borderColor={borderColor}
      boxShadow="rgba(100, 100, 111, 0.2) 0px 5px 8px 0px"
      transition="transform 0.25s ease-in-out"
      _hover={{ transform: 'scale(1.05)' }}
      position="relative"
      width="160px"
      display="flex"
      flexDirection="column"
    >
      {/* Container da imagem */}
      <Box position="relative" width="100%">
        <CImage
          src={cover}
          alt={book?.title}
          width="100%"
          aspectRatio={0.7}
          objectFit="cover"
          borderRadius="5px"
          border="1px solid #ededed"
          backgroundColor="white"
          boxShadow="rgba(100, 100, 111, 0.2) 0px 5px 8px 0px"
        />

        {/* Tipo de mídia / ícone de audiobook */}
        {book?.type === 'audiobook' ? (
          <Box position="absolute" top="5px" left="5px">
            <SlEarphones size={22} color="white" filter="drop-shadow(0px 0px 3px rgba(0,0,0,1))" />
          </Box>
        ) : (
          <Badge
            colorScheme={getTypeColor(book?.type)}
            position="absolute"
            top="6px"
            left="6px"
            fontSize="xs"
            px={2}
            py="2px"
            borderRadius="6px"
            zIndex={2}
          >
            {getTypeLabel(book?.type)}
          </Badge>
        )}

        {/* Medalhas de level */}
        {book?.levels && book.levels.length > 0 && (
          <VStack position="absolute" right="5px" top="5px" spacing="2px" zIndex={3}>
            {book.levels
              .map((l: any) => badges.find((badge: any) => badge.rank === l))
              .filter((badge: any) => badge !== undefined)
              .sort((a: any, b: any) => a.level - b.level)
              .map((badge: any, idx: number) => (
                <CImage
                  key={badge.rank + '-' + idx}
                  src={`/${badge.image}`}
                  alt={badge.name}
                  height="24px"
                  objectFit="contain"
                  filter="drop-shadow(0px 0px 3px rgba(0,0,0,1))"
                />
              ))}
          </VStack>
        )}
      </Box>

      {/* Container do conteúdo inferior */}
      <VStack align="flex-start" spacing="2px" mt={2} width="100%">
        <Text
          fontSize="12px"
          fontWeight="bold"
          color={titleColor}
          width="100%"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          title={book?.title}
        >
          {book?.title}
        </Text>

        {showAuthor && effectiveAuthor && (
          <Text
            fontSize="12px"
            color={subColor}
            width="100%"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {effectiveAuthor}
          </Text>
        )}

        {showDate && effectiveDate && (
          <Text fontSize="11px" color={subColor} width="100%" noOfLines={1}>
            {formatDateTime(effectiveDate)}
          </Text>
        )}

        {/* Botão de detalhes */}
        {showInfo && (
          <Popover placement="auto" isLazy>
            <PopoverTrigger>
              <Button
                size="xs"
                mt={1}
                variant="outline"
                colorScheme="blue"
                leftIcon={<FiInfo />}
                fontSize="xs"
                height="24px"
                px={2}
                width="100%"
                _hover={{ bg: 'blue.50', borderColor: 'blue.300' }}
              >
                Detalhes
              </Button>
            </PopoverTrigger>
            <Portal>
              <PopoverContent maxW="sm" boxShadow="xl" borderRadius="lg">
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverHeader fontWeight="bold" fontSize="sm" borderTopRadius="lg" bg="gray.50">
                  Detalhes do título
                </PopoverHeader>
                <PopoverBody>
                  <VStack align="stretch" spacing={3}>
                    {/* Cabeçalho com capa + título */}
                    <HStack align="flex-start" spacing={3}>
                      <CImage
                        src={cover}
                        alt={book?.title}
                        width="48px"
                        aspectRatio={0.7}
                        objectFit="cover"
                        borderRadius="6px"
                        border="1px solid #ededed"
                        flexShrink={0}
                      />
                      <VStack align="flex-start" spacing={0}>
                        <Text fontSize="sm" fontWeight="bold" color={titleColor} noOfLines={2}>
                          {book?.title || book?.book?.title || 'Título'}
                        </Text>
                        <Badge colorScheme={getTypeColor(book?.type)} fontSize="2xs" mt={1}>
                          {getTypeLabel(book?.type)}
                        </Badge>
                      </VStack>
                    </HStack>

                    {/* Data de entrega destacada */}
                    {effectiveDate && (
                      <Box bg="blue.50" p={2.5} borderRadius="md" borderLeft="4px solid" borderLeftColor="blue.400">
                        <HStack spacing={2} align="center">
                          <Icon as={FiCalendar} color="blue.500" />
                          <Box>
                            <Text fontSize="2xs" color="blue.600" fontWeight="semibold" textTransform="uppercase">
                              Entregue em
                            </Text>
                            <Text fontSize="xs" color="gray.700" fontWeight="medium">
                              {formatDateTime(effectiveDate)}
                            </Text>
                          </Box>
                        </HStack>
                      </Box>
                    )}

                    {/* Informações adicionais */}
                    <VStack align="stretch" spacing={1.5}>
                      {effectiveAuthor && (
                        <Text fontSize="xs" color="gray.600">
                          <b>Autor:</b> {effectiveAuthor}
                        </Text>
                      )}
                      {book?.isbn && (
                        <Text fontSize="xs" color="gray.600">
                          <b>{isMagazineType(book?.type) ? 'ISSN' : 'ISBN'}:</b> {book.isbn}
                        </Text>
                      )}
                      {effectiveDescription && (
                        <Text fontSize="xs" color="gray.600">
                          <b>Descrição:</b> {effectiveDescription}
                        </Text>
                      )}
                    </VStack>

                    {/* Identificador de Log */}
                    {showHash && effectiveHash && (
                      <Box bg="gray.50" p={2} borderRadius="md">
                        <HStack spacing={1.5} align="center">
                          <Icon as={FiHash} color="gray.400" boxSize={3} />
                          <Text fontSize="2xs" color="gray.500" fontWeight="semibold" textTransform="uppercase">
                            ID Log
                          </Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.600" fontFamily="mono" wordBreak="break-all" mt={1}>
                          {effectiveHash}
                        </Text>
                      </Box>
                    )}

                    {/* Canais de envio */}
                    <Box>
                      <Text fontSize="2xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" mb={1.5}>
                        Enviado por
                      </Text>
                      <HStack spacing={3}>
                        <HStack spacing={1} color="green.500">
                          <Icon as={FiBell} />
                          <Text fontSize="xs">Plataforma</Text>
                        </HStack>
                        <HStack spacing={1} color="green.500">
                          <Icon as={FiSmartphone} />
                          <Text fontSize="xs">Push</Text>
                        </HStack>
                        <HStack spacing={1} color="gray.400">
                          <Icon as={FiMail} />
                          <Text fontSize="xs">Email</Text>
                        </HStack>
                        <HStack spacing={1} color="gray.400">
                          <Icon as={FiMessageSquare} />
                          <Text fontSize="xs">SMS</Text>
                        </HStack>
                      </HStack>
                    </Box>

                    <Divider />
                    <Button
                      size="sm"
                      leftIcon={<FaDownload />}
                      colorScheme="blue"
                      isLoading={downloadMutation.isLoading}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload();
                      }}
                      isDisabled={!book?.id}
                    >
                      Download
                    </Button>
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </Portal>
          </Popover>
        )}
      </VStack>

      {/* Modal completo de faixas de audiobook */}
      <Modal isOpen={isAudioModalOpen} onClose={onCloseAudioModal} size="lg" isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="lg" onClick={(e) => e.stopPropagation()}>
          <ModalHeader borderBottom="1px solid" borderColor="gray.100" pb={3}>
            <HStack spacing={2}>
              <Icon as={SlEarphones} color="purple.500" boxSize={5} />
              <Text>Audiobook disponível para download</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4} py={2}>
              {/* Cabeçalho com capa + metadados */}
              <HStack align="flex-start" spacing={4}>
                <CImage
                  src={cover}
                  alt={book?.title}
                  width="64px"
                  aspectRatio={0.7}
                  objectFit="cover"
                  borderRadius="8px"
                  border="1px solid #ededed"
                  flexShrink={0}
                />
                <VStack align="flex-start" spacing={1} flex={1}>
                  <Text fontSize="md" fontWeight="bold" color={titleColor} noOfLines={2}>
                    {book?.title || 'Audiobook'}
                  </Text>
                  {effectiveAuthor && (
                    <Text fontSize="sm" color={subColor}>
                      {effectiveAuthor}
                    </Text>
                  )}
                  <HStack spacing={2} mt={1}>
                    <Badge colorScheme="purple" fontSize="2xs">
                      Audiobook
                    </Badge>
                    <Badge colorScheme="gray" variant="outline" fontSize="2xs">
                      {audioUrls.length} {audioUrls.length === 1 ? 'faixa' : 'faixas'}
                    </Badge>
                  </HStack>
                </VStack>
              </HStack>

              {/* Informações adicionais */}
              <VStack align="stretch" spacing={1.5} bg="gray.50" p={3} borderRadius="md">
                {book?.isbn && (
                  <Text fontSize="xs" color="gray.600">
                    <b>ISBN:</b> {book.isbn}
                  </Text>
                )}
                {effectiveDate && (
                  <Text fontSize="xs" color="gray.600">
                    <b>Entregue em:</b> {formatDateTime(effectiveDate)}
                  </Text>
                )}
                {showHash && effectiveHash && (
                  <Text fontSize="xs" color="gray.600" wordBreak="break-all">
                    <b>ID Log:</b> {effectiveHash}
                  </Text>
                )}
                {effectiveDescription && (
                  <Text fontSize="xs" color="gray.600">
                    <b>Descrição:</b> {effectiveDescription}
                  </Text>
                )}
              </VStack>

              <Divider />

              {/* Lista de faixas */}
              <VStack align="stretch" spacing={2}>
                <Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase">
                  Faixas de áudio
                </Text>
                {audioUrls.map((url, idx) => (
                  <HStack
                    key={url + idx}
                    justify="space-between"
                    p={2.5}
                    bg="purple.50"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="purple.100"
                  >
                    <HStack spacing={2} overflow="hidden">
                      <Icon as={FiBookOpen} color="purple.500" flexShrink={0} />
                      <VStack align="flex-start" spacing={0} overflow="hidden">
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                          Faixa {idx + 1}
                        </Text>
                        <Text fontSize="2xs" color="gray.500" noOfLines={1} title={getFileName(url)}>
                          {getFileName(url)}
                        </Text>
                      </VStack>
                    </HStack>
                    <HStack spacing={1}>
                      <Tooltip label="Ouvir faixa">
                        <IconButton
                          as="a"
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          aria-label="Ouvir faixa"
                          icon={<FiPlay />}
                          size="sm"
                          variant="ghost"
                          colorScheme="purple"
                        />
                      </Tooltip>
                      <Tooltip label="Baixar faixa">
                        <IconButton
                          as="a"
                          href={url}
                          download={getFileName(url)}
                          target="_blank"
                          rel="noreferrer"
                          aria-label="Baixar faixa"
                          icon={<FiDownloadCloud />}
                          size="sm"
                          colorScheme="purple"
                        />
                      </Tooltip>
                    </HStack>
                  </HStack>
                ))}
              </VStack>
            </VStack>
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={2}>
            <Button variant="outline" onClick={onCloseAudioModal}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
